import {signupPlanPath} from '../src/signup-route';
import {formatCredits} from '../src/credit-units';
import {useEffect, useId, useRef, useState, type FormEvent} from 'react';
import {EMAIL_PASSWORD_POLICY} from '../src/auth-policy';
import {ErrorNotice, Loading, shortId} from './components';
import {APP_BASE, Link, navigate, useSearch} from './router';
import type {AuthMode, HermesState} from './lib/types';
import type {hermesActions} from './lib/store';
import './auth.css';

type Props = {state: HermesState; actions: typeof hermesActions};
export type {AuthMode} from './lib/types';

function returnToWorkspace(search: string, returnTo?: string, registering=false): string {
  const requested = returnTo ?? new URLSearchParams(search).get('next');
  if(registering)return signupPlanPath(requested??undefined,APP_BASE);
  if (requested === null || requested === undefined) return `${APP_BASE}/${registering ? "subscribe" : "workspace"}`;
  const target = new URL(requested, window.location.origin);
  if (target.origin !== window.location.origin || !target.pathname.startsWith(`${APP_BASE}/`) ||
      ['login', 'register', 'setup-login'].some(page => target.pathname === `${APP_BASE}/${page}`)) {
    throw new Error('The sign-in return address must be a page in EightBots.');
  }
  return `${target.pathname}${target.search}`;
}

export function AccountIdentity({state, actions}: Props) {
  const authenticated = state.session.data;
  if (!authenticated) return null;
  return <div className="account-identity"><span>{authenticated.authMethod === 'google' ? 'Signed in with Google' : 'Signed in'}</span>
    <strong>{authenticated.email ?? (authenticated.address === null ? authenticated.accountId : shortId(authenticated.address))}</strong>
    <button type="button" className="text-button" disabled={state.operation.label !== null}
      onClick={() => void actions.signOut().catch(() => undefined)}>Sign out</button>
  </div>;
}

export function AuthPage({state, actions, mode, returnTo}: Props & {mode: AuthMode; returnTo?: string | undefined}) {
  const fieldId = useId();
  const search = useSearch();
  const destination = returnToWorkspace(search, returnTo,mode==='register');
  const selectedPlanId=new URL(destination,window.location.origin).searchParams.get('plan');
  useEffect(()=>{if(selectedPlanId)void actions.loadBillingPlans().catch(()=>undefined);},[actions,selectedPlanId]);
  const selectedPlan=state.billingPlans.data?.plans.find(plan=>plan.id===selectedPlanId);
  const [setupToken] = useState(() => mode === 'setup-login'
    ? new URLSearchParams(window.location.hash.slice(1)).get('token') : null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const busy = state.operation.label !== null;
  const registering = mode === 'register';
  const newPassword = mode !== 'login';
  const label = mode === 'login' ? 'Sign in' : registering ? 'Sign up' : 'Save sign-in details';
  const googleLabel = registering ? 'Sign up with Google' : 'Sign in with Google';
  const next = `?${new URLSearchParams({next: destination})}`;


  useEffect(() => {
    if (mode === 'setup-login' && window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [mode]);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get('email') ?? '');
    const password = String(data.get('password') ?? '');
    try {
      if (mode === 'login') await actions.signIn(email, password);
      else if (mode === 'register') await actions.register(email, password);
      else {
        if (setupToken === null) throw new Error('Open your account setup link before you set a password.');
        await actions.setupLogin(setupToken, email, password);
      }
      form.reset();
      if (`${window.location.pathname}${window.location.search}` !== destination) navigate(destination);
    } catch { /* The shared operation error is shown below. */ }
  }

  return <section className="auth-page" aria-labelledby={`${fieldId}-title`}>
    <div className="auth-content"><header><h1 id={`${fieldId}-title`}>{mode === 'login' ? 'Sign in to your account' : registering ? 'Create an account' : 'Set up your sign-in'}</h1>
      {mode === 'setup-login' && <p>Choose an email and password for your existing account. Your saved work stays in this account.</p>}</header>
      {selectedPlan && <div className="auth-plan-summary"><strong>{selectedPlan.name}</strong><span>${(selectedPlan.unitAmount/100).toFixed(2)} / {selectedPlan.interval}</span><p>{formatCredits(selectedPlan.periodCredits)} credits, divided into weekly limits.</p><Link href={`${APP_BASE}/subscribe`}>Change plan</Link></div>}
      {state.session.status === 'loading' || state.session.status === 'idle' ? <Loading/>
        : state.session.status === 'error' ? <><ErrorNotice error={state.session.error}/><button type="button" className="text-button" onClick={() => void actions.refresh().catch(() => undefined)}>Retry account connection</button></>
          : mode === 'setup-login' && setupToken === null ? <div className="auth-link-required"><p>Open your account setup link to continue.</p><Link className="button" href={`${APP_BASE}/login${next}`}>Go to sign-in</Link></div>
            : <form className="auth-form" onSubmit={event => void submit(event)}>
              {mode !== 'setup-login' && <div className="auth-google">
                <button type="button" className="button auth-google-button" disabled={busy || state.config.status !== 'ready' || !state.config.data?.googleConfigured}
                  onClick={() => void actions.signInGoogle(destination, mode).catch(() => undefined)}><GoogleLogo/><span>{googleLabel}</span></button>
                {state.config.status === 'ready' && !state.config.data?.googleConfigured && <p className="auth-google-unavailable">Google sign-in setup is in progress.</p>}
                <p className="auth-legal">By clicking “{googleLabel}” I agree to the <TermsLink/>, and acknowledge EightBots’ <PrivacyLink/>.</p>
              </div>}
              <div className="auth-field"><label htmlFor={`${fieldId}-email`}>Email</label><input id={`${fieldId}-email`} type="email" name="email" autoComplete="username" autoCapitalize="none"
                autoCorrect="off" spellCheck={false} maxLength={EMAIL_PASSWORD_POLICY.emailMaxLength} required disabled={busy}/></div>
              <div className="auth-field"><label htmlFor={`${fieldId}-password`}>Password</label>
                <div className="auth-password"><input id={`${fieldId}-password`} type={showPassword ? 'text' : 'password'} name="password"
                  autoComplete={newPassword ? 'new-password' : 'current-password'} required disabled={busy}
                  minLength={newPassword ? EMAIL_PASSWORD_POLICY.minLength : undefined} maxLength={EMAIL_PASSWORD_POLICY.maxLength}
                  onFocus={() => setPasswordFocused(true)} onBlur={() => setPasswordFocused(false)}
                  aria-describedby={newPassword ? `${fieldId}-password-help` : undefined}/>
                  <button type="button" className="auth-password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-controls={`${fieldId}-password`} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)}><PasswordEye revealed={showPassword}/></button>
                </div>
                {newPassword && <p id={`${fieldId}-password-help`} className="auth-password-help" data-visible={passwordFocused}>Use at least {EMAIL_PASSWORD_POLICY.minLength} characters.</p>}
              </div>
              {registering && <div className="auth-consent"><input id={`${fieldId}-terms`} type="checkbox" name="terms" required disabled={busy}/>
                <label htmlFor={`${fieldId}-terms`}>I agree to the <TermsLink/>, and acknowledge EightBots’ <PrivacyLink/></label></div>}
              <ErrorNotice error={state.operation.scope === mode ? state.operation.error : null}/>
              <button className="button auth-submit" type="submit" disabled={busy}>{label}</button>
              {mode !== 'setup-login' && <p className="auth-switch">{mode === 'login' ? 'Don’t have an account?' : 'Already registered?'}{' '}
                <Link href={`${APP_BASE}/${mode === 'login' ? 'register' : 'login'}${next}`}>{mode === 'login' ? 'Sign up' : 'Sign in'}</Link></p>}
            </form>}
    </div>
  </section>;
}

function TermsLink() {
  return <Link href={`${APP_BASE}/terms`} target="_blank" rel="noopener noreferrer">Terms of Service</Link>;
}

function PrivacyLink() {
  return <Link href={`${APP_BASE}/privacy`} target="_blank" rel="noopener noreferrer">Privacy Policy</Link>;
}

function GoogleLogo() {
  return <svg className="auth-google-logo" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.36Z"/>
    <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.42l-3.24-2.51c-.9.6-2.05.96-3.38.96-2.6 0-4.8-1.76-5.58-4.12H3.08v2.59A10 10 0 0 0 12 22Z"/>
    <path fill="#FBBC05" d="M6.42 13.91a6 6 0 0 1 0-3.82V7.5H3.08a10 10 0 0 0 0 9l3.34-2.59Z"/>
    <path fill="#EA4335" d="M12 5.97c1.47 0 2.79.5 3.83 1.5l2.88-2.88A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.92 5.5l3.34 2.59A5.99 5.99 0 0 1 12 5.97Z"/>
  </svg>;
}

function PasswordEye({revealed}: {revealed: boolean}) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/>
    {revealed && <path d="m3 3 18 18"/>}
  </svg>;
}

export function AuthDialog({state,actions}:Props){
  const ref=useRef<HTMLDialogElement>(null),[route,setRoute]=useState<string|null>(null),[mode,setMode]=useState<AuthMode>('register');
  useEffect(()=>{
    const open=(event:Event)=>{
      if(state.session.data)return;
      const href=(event as CustomEvent<{href:string}>).detail.href,url=new URL(href,window.location.origin);
      if(!['/login','/register'].some(path=>url.pathname===`${APP_BASE}${path}`)){ref.current?.close();return;}
      if(!ref.current?.open&&['/login','/register','/setup-login'].some(path=>window.location.pathname===`${APP_BASE}${path}`))return;
      event.preventDefault();setRoute(href);setMode(url.pathname.endsWith('/register')?'register':'login');ref.current?.showModal();
    };
    window.addEventListener('eightbots-auth-navigation',open);return()=>window.removeEventListener('eightbots-auth-navigation',open);
  },[state.session.data]);
  useEffect(()=>{if(state.session.data)ref.current?.close();},[state.session.data]);
  return <dialog ref={ref} className="auth-dialog" aria-label={mode==='register'?'Create your EightBots account':'Sign in to EightBots'} onClose={()=>setRoute(null)} onClick={event=>{
    if(event.target!==event.currentTarget)return;
    const bounds=event.currentTarget.getBoundingClientRect();
    if(event.clientX<bounds.left||event.clientX>bounds.right||event.clientY<bounds.top||event.clientY>bounds.bottom)event.currentTarget.close();
  }}>
    <div className="auth-dialog-toolbar"><button type="button" className="auth-dialog-close" aria-label="Close account window" onClick={()=>ref.current?.close()}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
    </button></div>
    <div className="auth-dialog-body">{route&&<AuthPage key={mode} state={state} actions={actions} mode={mode} returnTo={new URL(route,window.location.origin).searchParams.get('next')??undefined}/>}</div>
  </dialog>;
}
